import React, { useEffect, useMemo, useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Academy } from '../../hooks/api/academy';
import { Booking, useGetBookings } from '../../hooks/api/booking';
import { useGetLessons } from '../../hooks/api/lesson';
import {
  addDaysToDate,
  convertDateToDateString,
  formatDateToWeekday,
  formatDateToWeekRange,
  getStartOfWeek,
  parseDateInputValue,
} from '../../util/dates';
import HomeLessonsList from './HomeLessonsList';
import './lessonsView.scss';

interface DefaultLessonsViewProps {
  academies: Academy[];
  academyId: number;
  date: string;
  isInstructor: boolean;
  onAcademyChange: (academyId: number) => void;
  onCreateLesson: () => void;
  onDateChange: (date: string) => void;
  userId: string;
}

const EMPTY_BOOKINGS: Booking[] = [];

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export default function DefaultLessonsView({
  academies,
  academyId,
  date,
  isInstructor,
  onAcademyChange,
  onCreateLesson,
  onDateChange,
  userId,
}: DefaultLessonsViewProps) {
  const selectedDate = useMemo(() => parseDateInputValue(date), [date]);
  const lessonsQuery = useGetLessons({ date: selectedDate, academyId });
  const bookingsQuery = useGetBookings({ date: selectedDate });

  const lessons = lessonsQuery.data ?? [];
  const bookings = bookingsQuery.data ?? EMPTY_BOOKINGS;
  const bookingsByLessonId = useMemo(() => {
    const lessonBookingsMap = new Map<number, Booking[]>();

    bookings.forEach((booking) => {
      const lessonBookings = lessonBookingsMap.get(booking.lessonId);
      if (lessonBookings) {
        lessonBookings.push(booking);
        return;
      }

      lessonBookingsMap.set(booking.lessonId, [booking]);
    });

    return lessonBookingsMap;
  }, [bookings]);

  // Mon–Sun strip around the selected day; chevrons jump a full week while
  // keeping the same weekday selected.
  const weekStart = useMemo(() => getStartOfWeek(selectedDate), [selectedDate]);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDaysToDate(weekStart, index)),
    [weekStart],
  );
  const todayString = convertDateToDateString(new Date());

  const shortSelectedDate = capitalize(
    selectedDate
      .toLocaleDateString(navigator.language, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      })
      .replace(/\./g, ''),
  );

  const goToRelativeWeek = (weekOffset: number) =>
    onDateChange(convertDateToDateString(addDaysToDate(selectedDate, weekOffset * 7)));

  // Keep the active pill centred as the selection moves.
  const activeDayRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    activeDayRef.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [date]);

  const isLoading = lessonsQuery.isLoading;
  const lessonCountLabel = `${lessons.length} ${lessons.length === 1 ? 'clase' : 'clases'}`;

  return (
    <div className="lessons-view">
      <header className="lv-head">
        <div className="lv-head__left">
          <h1 className="lv-head__title">Clases</h1>
          <span className="lv-head__date">{shortSelectedDate}</span>
        </div>
        {academies.length > 1 && (
          <div className="lv-academy">
            <select
              aria-label="Academia"
              value={academyId}
              onChange={(event) => onAcademyChange(Number(event.target.value))}
            >
              {academies.map((academy) => (
                <option key={academy.id} value={academy.id}>
                  {academy.name}
                </option>
              ))}
            </select>
            <ExpandMoreIcon className="lv-academy__chev" />
          </div>
        )}
      </header>

      <div className="lv-weeknav">
        <span className="lv-weeknav__range">{formatDateToWeekRange(weekStart)}</span>
        <div className="lv-weeknav__actions">
          <button
            type="button"
            className="lv-today"
            onClick={() => onDateChange(todayString)}
          >
            Hoy
          </button>
          <button
            type="button"
            className="lv-iconbtn"
            aria-label="Semana anterior"
            onClick={() => goToRelativeWeek(-1)}
          >
            <ChevronLeftIcon />
          </button>
          <button
            type="button"
            className="lv-iconbtn"
            aria-label="Semana siguiente"
            onClick={() => goToRelativeWeek(1)}
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>

      <div className="lv-days">
        {weekDays.map((day) => {
          const dayString = convertDateToDateString(day);
          const isActive = dayString === date;
          const isToday = dayString === todayString;
          return (
            <button
              key={dayString}
              type="button"
              ref={isActive ? activeDayRef : undefined}
              className={`lv-day${isActive ? ' lv-day--active' : ''}`}
              onClick={() => onDateChange(dayString)}
            >
              <span className="lv-day__dow">
                {formatDateToWeekday(day, { weekday: 'short' }).replace('.', '')}
              </span>
              <span className="lv-day__num">{day.getDate()}</span>
              {isToday && <span className="lv-day__dot" />}
            </button>
          );
        })}
      </div>

      <div className="lv-bar">
        <span className="lv-bar__count">{isLoading ? 'Cargando' : lessonCountLabel}</span>
        <span className="lv-bar__rule" />
      </div>

      {isInstructor && (
        <button type="button" className="lv-add" onClick={onCreateLesson}>
          <AddIcon />
          Añadir clase
        </button>
      )}

      {isLoading ? (
        <>
          <div className="lv-skeleton" />
          <div className="lv-skeleton" />
        </>
      ) : lessons.length === 0 ? (
        <div className="lv-empty">
          <h2 className="lv-empty__title">Día sin clases</h2>
          <p className="lv-empty__text">No hay clases programadas para este día</p>
        </div>
      ) : (
        <HomeLessonsList
          bookingsByLessonId={bookingsByLessonId}
          currentUserId={userId}
          lessons={lessons}
          variant="default"
        />
      )}
    </div>
  );
}
